import { Button } from "@/components/ui/button";
import { Menu, Linkedin } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiLabbetLogo from "@/assets/ai-labbet-white.png";
import { trackEvent } from "@/lib/analytics";
import { useLanguage } from "@/i18n/useLanguage";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { lang, switchTo, localize } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActiveLink = (href: string) => {
    const localized = localize(href);
    if (localized === location.pathname) return true;
    if (href === "/" && (location.pathname === "/" || location.pathname === "/sv")) return true;
    return false;
  };

  const menuItems = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.aiSkilling"), href: "/ai-skilling" },
    { label: t("nav.services"), href: "/services" },
    { label: t("nav.news"), href: "/news" },
    { label: t("nav.contact"), href: "/contact" },
  ];

  const LanguageToggle = ({ className }: { className?: string }) => (
    <div className={cn("inline-flex items-center text-xs font-semibold tracking-wider rounded-full border border-border/60 overflow-hidden", className)}>
      <button
        type="button"
        onClick={() => switchTo("en")}
        aria-label={t("language.switchToEnglish")}
        aria-pressed={lang === "en"}
        className={cn(
          "px-2.5 py-1 transition-colors",
          lang === "en" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"
        )}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => switchTo("sv")}
        aria-label={t("language.switchToSwedish")}
        aria-pressed={lang === "sv"}
        className={cn(
          "px-2.5 py-1 transition-colors",
          lang === "sv" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"
        )}
      >
        SV
      </button>
    </div>
  );

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b transition-all duration-300 ${
      isScrolled 
        ? "border-border shadow-[0_4px_12px_rgba(0,0,0,0.3)]" 
        : "border-border/50"
    }`}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={localize("/")} className="flex items-center">
            <img 
              src={aiLabbetLogo} 
              alt="AI-labbet - Business value through responsible AI" 
              className="h-7 md:h-8 lg:h-9 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-10">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={localize(item.href)}
                className={`text-sm font-medium transition-colors ${
                  isActiveLink(item.href)
                    ? "text-primary font-semibold border-b-2 border-primary pb-1"
                    : "text-foreground/80 hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <LanguageToggle />
            <Button variant="secondary" size="sm" asChild>
              <a
                href="https://www.linkedin.com/company/ai-labbet/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center"
                onClick={() =>
                  trackEvent("outbound_click", { destination: "linkedin", location: "header_desktop" })
                }
              >
                <Linkedin className="w-4 h-4 mr-2" />
                {t("nav.followUs")}
              </a>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageToggle />
            <button
              className="text-foreground"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={t("nav.toggleMenu")}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 flex flex-col gap-4">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={localize(item.href)}
                className={`text-sm font-medium transition-colors ${
                  isActiveLink(item.href)
                    ? "text-primary font-semibold"
                    : "text-foreground/80 hover:text-foreground"
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Button variant="secondary" size="sm" className="w-full" asChild>
              <a
                href="https://www.linkedin.com/company/ai-labbet/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center"
                onClick={() =>
                  trackEvent("outbound_click", { destination: "linkedin", location: "header_mobile" })
                }
              >
                <Linkedin className="w-4 h-4 mr-2" />
                {t("nav.followUs")}
              </a>
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
